/**
 * Detects non-actionable transport failures of next-auth's
 * `GET /api/auth/session` fetch.
 *
 * `SessionProvider` refetches the session on window focus, so a popup or tab
 * teardown, a sleep/wake, or a momentary connectivity drop aborts the request
 * at the transport layer. next-auth's `fetchData` catches it and calls
 * `logger.error("CLIENT_FETCH_ERROR", ...)`, and the browser's own failed
 * fetch is reported separately. One abort therefore reaches Datadog three
 * times:
 *
 *  1. A **console-origin** signal — next-auth's `console.error`, whose text
 *     carries both `CLIENT_FETCH_ERROR` and the session URL, and which
 *     reaches RUM as an error event and Browser Logs as a log. It has no
 *     `http.url` or `http.status_code`, and RUM gives it no stack.
 *
 *  2. A **network-origin** signal — the failed fetch itself, with the session
 *     URL in `http.url` and status code 0.
 *
 * The server side is healthy in every observed case (the endpoint returns
 * 200), so there is nothing to act on in application code.
 */
export function isAuthSessionFetchNetworkNoise({
  message,
  errorMessage,
  stack,
  url,
  statusCode,
}: {
  message: string | undefined;
  errorMessage: string | undefined;
  stack: string | undefined;
  url: string | undefined;
  statusCode: number | undefined;
}): boolean {
  const normalizedMessage = message?.toLowerCase() ?? "";
  const normalizedErrorMessage = errorMessage?.toLowerCase() ?? "";
  const normalizedStack = stack?.toLowerCase() ?? "";

  // Console-origin logs have no http.url or status code. Only ignore a
  // next-auth session error when its serialized cause is a transport failure.
  const consoleError = message?.match(
    /^\[next-auth\]\[error\]\[CLIENT_FETCH_ERROR\]\s+https:\/\/next-auth\.js\.org\/errors#client_fetch_error\s+(.+?)\s+(\{.*\})$/s,
  );
  if (consoleError) {
    try {
      const metadata = JSON.parse(consoleError[2]) as {
        error?: { name?: string; message?: string };
        url?: string;
        message?: string;
      };
      const cause = metadata?.error;
      // Real payloads carry trailing whitespace in the message (Firefox sends
      // "The operation was aborted. "), and the regex's `\s+` strips it from
      // group 1. Trim every value in the comparisons below so they agree.
      const causeMessage = cause?.message?.trim() ?? "";
      const isTransportFailure =
        (cause?.name === "TypeError" &&
          [
            "Failed to fetch",
            "Load failed",
            "NetworkError when attempting to fetch resource.",
            // Safari reports an aborted fetch as TypeError "cancelled".
            "cancelled",
          ].includes(causeMessage)) ||
        (cause?.name === "AbortError" &&
          ["The operation was aborted.", "This operation was aborted"].includes(
            causeMessage,
          ));

      return (
        metadata.url === "/api/auth/session" &&
        (metadata.message?.trim() ?? "") === causeMessage &&
        consoleError[1].trim() === causeMessage &&
        isTransportFailure
      );
    } catch {
      return false;
    }
  }

  // Safari emits "cancelled" (with status_code 0) when a pending fetch is
  // aborted — e.g. after a ChunkLoadError triggers a page reload.
  const networkErrorSnippets = [
    "networkerror when attempting to fetch resource",
    "failed to fetch",
    "load failed",
    "cancelled",
  ];

  const hasNetworkErrorSignal = networkErrorSnippets.some((snippet) => {
    return (
      normalizedMessage.includes(snippet) ||
      normalizedErrorMessage.includes(snippet) ||
      normalizedStack.includes(snippet)
    );
  });

  if (!hasNetworkErrorSignal) {
    return false;
  }

  // Network-origin logs: the session URL is in http.url with status_code 0.
  const normalizedUrl = url?.toLowerCase() ?? "";
  const isAuthSessionEndpoint =
    normalizedUrl === "https://app.marblehealth.com/api/auth/session" ||
    normalizedUrl.endsWith("/api/auth/session");

  if (isAuthSessionEndpoint && statusCode === 0) {
    return true;
  }

  return false;
}
